<template lang = "pug">
fi-modal.customer-veriff-modal(
  ref     = 'modal'
  v-model = 'showModal'
  size    = 'lg'
  :title  = '$t("title")'
  lazy
  @shown  = 'onShown'
  @hide   = 'onHide'
)
  v-loading(loader = 'veriff:session:fetch')
    template(#spinner)
      .text-center.py-4
        i.fa.fa-spinner.fa-pulse.fa-2x
        .mt-2 {{ $t('loading') }}

    // Customer Identity Summary Header
    .card.mb-3
      .card-body
        .row.align-items-center
          .col-md-4.font-weight-bold {{ $t('customer') }}:
          .col-md-8 {{ customerName(customer) }} ({{ customer.idCode }})
        hr.my-2
        .row.align-items-center
          .col-md-4.font-weight-bold {{ $t('verificationStatus') }}:
          .col-md-8
            span.badge.p-2(:class = 'isVeriffApproved ? "badge-success text-white" : "badge-warning text-dark"')
              i.mr-1(:class = 'isVeriffApproved ? "fa fa-check-circle" : "fa fa-exclamation-circle"')
              | {{ isVeriffApproved ? $t('identityAuthenticated') : $t('notAuthenticated') }}

        template(v-if = 'latestSession')
          hr.my-2
          .row.align-items-center
            .col-md-4.font-weight-bold {{ $t('lastSessionStatus') }}:
            .col-md-8
              span.badge.p-2(:class = 'statusBadgeClass(latestSession.status)')
                | {{ statusLabel(latestSession.status) }}
              span.ml-2.text-muted(v-if = 'latestSession.verificationCode')
                | ({{ $t('code') }}: {{ latestSession.verificationCode }})

          template(v-if = 'latestSession.documentNumber')
            hr.my-2
            .row.align-items-center
              .col-md-4.font-weight-bold {{ $t('verifiedDocument') }}:
              .col-md-8
                | {{ latestSession.documentType }} - {{ latestSession.documentNumber }} ({{ latestSession.documentCountry }})

          template(v-if = 'latestSession.pepStatus || latestSession.sanctionsStatus')
            hr.my-2
            .row.align-items-center
              .col-md-4.font-weight-bold {{ $t('amlScreening') }}:
              .col-md-8
                span.badge.mr-2(:class = 'latestSession.pepStatus === "MATCH" ? "badge-danger" : "badge-secondary"')
                  | PEP: {{ latestSession.pepStatus || 'N/A' }}
                span.badge(:class = 'latestSession.sanctionsStatus === "MATCH" ? "badge-danger" : "badge-secondary"')
                  | Sanctions: {{ latestSession.sanctionsStatus || 'N/A' }}

    // Active or newly generated Session Link
    div(v-if = 'latestSession && latestSession.sessionUrl && (latestSession.status === "CREATED" || latestSession.status === "EMAIL_SENT")')
      .alert.alert-info
        .font-weight-bold.mb-1
          i.fa.fa-link.mr-1
          | {{ $t('sessionLinkTitle') }}
        .text-break.small.mb-2 {{ latestSession.sessionUrl }}
        .d-flex.align-items-center.mt-2
          button.btn.btn-sm.btn-primary(@click = 'copySessionUrl')
            i.far.fa-copy.mr-1
            | {{ $t('copyLink') }}
          button.btn.btn-sm.btn-info.ml-2(
            :disabled = 'sendingEmail'
            @click    = 'sendVerificationEmail'
          )
            i.far.fa-envelope.mr-1
            | {{ $t('sendEmailToCustomer') }}
            i.fa.fa-spinner.fa-pulse.ml-1(v-if = 'sendingEmail')

    // Verification History Section
    div(v-if = 'history && history.length > 0')
      h6.font-weight-bold.mt-4.mb-2 {{ $t('historyTitle') }}
      .table-responsive
        table.table.table-sm.table-striped
          thead: tr
            th {{ $t('date') }}
            th {{ $t('status') }}
            th {{ $t('document') }}
            th {{ $t('pepSanctions') }}
          tbody
            tr(v-for = 'item in history' :key = 'item.id')
              td {{ $t('common:formatDatetime', { value: item.createdAt }) }}
              td
                span.badge(:class = 'statusBadgeClass(item.status)') {{ statusLabel(item.status) }}
              td {{ item.documentNumber ? (item.documentType + " " + item.documentNumber) : "-" }}
              td
                span.badge.mr-1(v-if = 'item.pepStatus' :class = 'item.pepStatus === "MATCH" ? "badge-danger" : "badge-light"')
                  | PEP: {{ item.pepStatus }}
                span.badge(v-if = 'item.sanctionsStatus' :class = 'item.sanctionsStatus === "MATCH" ? "badge-danger" : "badge-light"')
                  | Sanctions: {{ item.sanctionsStatus }}

  template(#modal-footer)
    button.btn.btn-success(
      :disabled = 'creating'
      @click    = 'createSession'
    )
      i.fa.fa-id-card.mr-1
      | {{ latestSession ? $t('recreateSession') : $t('startVerification') }}
      i.fa.fa-spinner.fa-pulse.ml-1(v-if = 'creating')

    button.btn.btn-secondary(
      v-if      = 'latestSession'
      :disabled = 'refreshing'
      @click    = 'fetchData'
    )
      i.fa.fa-sync-alt.mr-1
      | {{ $t('refresh') }}
      i.fa.fa-spinner.fa-pulse.ml-1(v-if = 'refreshing')

    button.btn.btn-outline-secondary(@click = 'closeModal') {{ $t('common:close') }}
</template>

<script>
import FiModal from '@/components/FiModal'
import api from '@/api'
import { mapGetters } from 'vuex'

export default {
  name: 'customer-veriff-modal',

  components: { FiModal },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    latestSession: null,
    history: [],
    creating: false,
    refreshing: false,
    sendingEmail: false
  }),

  computed: {
    ...mapGetters('customers', ['customerName']),

    showModal: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },

    isVeriffApproved () {
      return Boolean(
        (this.customer && this.customer.identityAuthenticated) ||
        (this.latestSession && this.latestSession.status === 'APPROVED')
      )
    }
  },

  methods: {
    async onShown () {
      await this.fetchData()
    },

    onHide () {
      this.$emit('input', false)
    },

    closeModal () {
      this.showModal = false
    },

    async fetchData () {
      if (!this.customer || !this.customer.id) return
      this.refreshing = true
      try {
        const [latestRes, historyRes] = await Promise.allSettled([
          api.getLatestVeriffSession({ customerId: this.customer.id }),
          api.getVeriffSessionHistory({ customerId: this.customer.id })
        ])

        if (latestRes.status === 'fulfilled' && latestRes.value) {
          this.latestSession = latestRes.value.data || null
          if (this.latestSession && this.latestSession.status === 'APPROVED' && this.customer && !this.customer.identityAuthenticated) {
            this.$emit('changed')
          }
        } else {
          this.latestSession = null
        }

        if (historyRes.status === 'fulfilled' && historyRes.value) {
          this.history = historyRes.value.data || []
        } else {
          this.history = []
        }
      } finally {
        this.refreshing = false
      }
    },

    async createSession () {
      this.creating = true
      try {
        const response = await api.createVeriffSession({ customerId: this.customer.id })
        this.latestSession = response?.data || null
        this.$toasted.success(this.$t('sessionCreatedSuccess'))
        await this.fetchData()
        this.$emit('changed')
      } catch (err) {
        const msg = err.response?.data?.message || err.message
        this.$toasted.error(msg || this.$t('sessionCreateFailed'))
      } finally {
        this.creating = false
      }
    },

    copySessionUrl () {
      if (this.latestSession && this.latestSession.sessionUrl) {
        navigator.clipboard.writeText(this.latestSession.sessionUrl)
        this.$toasted.success(this.$t('urlCopied'))
      }
    },

    async sendVerificationEmail () {
      if (!this.customer || !this.customer.id) return
      this.sendingEmail = true
      try {
        const response = await api.sendVeriffVerificationEmail({ customerId: this.customer.id })
        if (response && response.data) {
          this.latestSession = response.data
        }
        this.$toasted.success(this.$t('emailSentSuccess'))
        await this.fetchData()
        this.$emit('changed')
      } catch (err) {
        const msg = err.response?.data?.message || err.message
        this.$toasted.error(msg || this.$t('emailSendFailed'))
      } finally {
        this.sendingEmail = false
      }
    },

    statusLabel (status) {
      if (!status) return ''
      switch (status) {
        case 'CREATED': return this.$t('statusCreated')
        case 'EMAIL_SENT': return this.$t('statusEmailSent')
        case 'STARTED': return this.$t('statusStarted')
        case 'SUBMITTED': return this.$t('statusSubmitted')
        case 'APPROVED': return this.$t('statusApproved')
        case 'DECLINED': return this.$t('statusDeclined')
        case 'RESUBMISSION_REQUESTED': return this.$t('statusResubmit')
        case 'EXPIRED': return this.$t('statusExpired')
        case 'ABANDONED': return this.$t('statusAbandoned')
        default: return status
      }
    },

    statusBadgeClass (status) {
      switch (status) {
        case 'APPROVED': return 'badge-success text-white'
        case 'DECLINED': return 'badge-danger text-white'
        case 'RESUBMISSION_REQUESTED': return 'badge-warning text-dark'
        case 'CREATED':
        case 'EMAIL_SENT':
        case 'STARTED': return 'badge-info text-white'
        case 'SUBMITTED': return 'badge-primary text-white'
        default: return 'badge-secondary'
      }
    }
  }
}
</script>

<i18n>
en:
  title: "Veriff Identity Verification"
  loading: "Loading Veriff verification status..."
  customer: "Customer"
  verificationStatus: "Authentication Status"
  identityAuthenticated: "Identity Verified"
  notAuthenticated: "Not Verified"
  lastSessionStatus: "Last Verification Status"
  code: "Code"
  verifiedDocument: "Verified Document"
  amlScreening: "AML / PEP / Sanctions"
  sessionLinkTitle: "Customer Verification Link"
  copyLink: "Copy Link"
  urlCopied: "Verification link copied to clipboard!"
  sendEmailToCustomer: "Send Email to Customer"
  emailSentSuccess: "Verification link sent to customer email!"
  emailSendFailed: "Failed to send verification email"
  historyTitle: "Verification History"
  date: "Date"
  status: "Status"
  document: "Document"
  pepSanctions: "PEP / Sanctions"
  startVerification: "Verify Customer with Veriff"
  recreateSession: "New Verification Session"
  refresh: "Refresh Status"
  sessionCreatedSuccess: "Veriff session created successfully!"
  sessionCreateFailed: "Failed to create Veriff session"
  statusCreated: "Link Created (Awaiting Customer)"
  statusEmailSent: "Email Sent for Verification"
  statusStarted: "Verification Started"
  statusSubmitted: "Under Review"
  statusApproved: "Identity Verified"
  statusDeclined: "Declined"
  statusResubmit: "Resubmission Requested"
  statusExpired: "Expired"
  statusAbandoned: "Abandoned"
et:
  title: "Veriff Isikutuvastus"
  loading: "Veriff staatuse laadimine..."
  customer: "Klient"
  verificationStatus: "Tuvastamise staatus"
  identityAuthenticated: "Isik tuvastatud"
  notAuthenticated: "Tuvastamata"
  lastSessionStatus: "Viimase seansi staatus"
  code: "Kood"
  verifiedDocument: "Tuvastatud dokument"
  amlScreening: "AML / PEP / Sanktsioonid"
  sessionLinkTitle: "Kliendi tuvastuslink"
  copyLink: "Kopeeri link"
  urlCopied: "Tuvastuslink kopeeritud lõikelauale!"
  sendEmailToCustomer: "Saada kliendile e-postiga"
  emailSentSuccess: "Tuvastuslink saadeti kliendi e-postile!"
  emailSendFailed: "E-posti saatmine ebaõnnestus"
  historyTitle: "Tuvastamise ajalugu"
  date: "Kuupäev"
  status: "Staatus"
  document: "Dokument"
  pepSanctions: "PEP / Sanktsioonid"
  startVerification: "Tuvasta klient Veriffis"
  recreateSession: "Uus tuvastusseanss"
  refresh: "Värskenda staatust"
  sessionCreatedSuccess: "Veriff seanss loodud edukalt!"
  sessionCreateFailed: "Veriff seansi loomine ebaõnnestus"
  statusCreated: "Link loodud (Ootab klienti)"
  statusEmailSent: "E-post saadetud tuvastamiseks"
  statusStarted: "Alustatud"
  statusSubmitted: "Ülevaatamisel"
  statusApproved: "Kinnitatud"
  statusDeclined: "Tagasi lükatud"
  statusResubmit: "Uuesti esitamise nõue"
  statusExpired: "Aegunud"
  statusAbandoned: "Katkestatud"
ru:
  title: "Идентификация личности через Veriff"
  loading: "Загрузка статуса Veriff..."
  customer: "Клиент"
  verificationStatus: "Статус идентификации"
  identityAuthenticated: "Личность подтверждена"
  notAuthenticated: "Не идентифицирован"
  lastSessionStatus: "Статус последней сессии"
  code: "Код"
  verifiedDocument: "Документ"
  amlScreening: "AML / PEP / Санкции"
  sessionLinkTitle: "Ссылка для идентификации"
  copyLink: "Копировать ссылку"
  urlCopied: "Ссылка скопирована!"
  sendEmailToCustomer: "Отправить клиенту по эл. почте"
  emailSentSuccess: "Ссылка для идентификации отправлена на эл. почту клиента!"
  emailSendFailed: "Не удалось отправить эл. письмо"
  historyTitle: "История идентификации"
  date: "Дата"
  status: "Статус"
  document: "Документ"
  pepSanctions: "PEP / Санкции"
  startVerification: "Идентифицировать через Veriff"
  recreateSession: "Создать новую сессию"
  refresh: "Обновить статус"
  sessionCreatedSuccess: "Сессия Veriff успешно создана!"
  sessionCreateFailed: "Не удалось создать сессию Veriff"
  statusCreated: "Ссылка создана"
  statusEmailSent: "Письмо отправлено для идентификации"
  statusStarted: "Начато"
  statusSubmitted: "На проверке"
  statusApproved: "Подтверждено"
  statusDeclined: "Отклонено"
  statusResubmit: "Требуется повторная подача"
  statusExpired: "Истекло"
  statusAbandoned: "Отменено клиентом"
</i18n>
